import {
  Color, HalfFloatType, Line, Mesh, Object3D, Points, Sprite, Vector2,
  type PerspectiveCamera, type Scene, type WebGLRenderer, WebGLRenderTarget
} from "three";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { GTAOPass } from "three/examples/jsm/postprocessing/GTAOPass.js";
import { OutputPass } from "three/examples/jsm/postprocessing/OutputPass.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { ShaderPass } from "three/examples/jsm/postprocessing/ShaderPass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";
import { Reflector } from "three/examples/jsm/objects/Reflector.js";

/** Keep the effect affordable even on a high-DPI or very large display. */
export function contactShadowSize(width: number, height: number, pixelRatio: number) {
  const w = Math.max(1, width * pixelRatio);
  const h = Math.max(1, height * pixelRatio);
  const scale = Math.min(0.8, Math.sqrt(900_000 / (w * h)));
  return { width: Math.max(1, Math.floor(w * scale)), height: Math.max(1, Math.floor(h * scale)) };
}

export function excludeFromContactShadows(object: Object3D) {
  if (object instanceof Reflector || object instanceof Sprite || object instanceof Line || object instanceof Points) return true;
  if (!(object instanceof Mesh)) return false;
  const materials = Array.isArray(object.material) ? object.material : [object.material];
  return object.userData.glassSurface === true || materials.some((material) =>
    material.transparent && (!material.depthWrite || material.opacity < 0.98)
  );
}

class FurnitureContactShadowPass extends GTAOPass {
  override render(renderer: WebGLRenderer, writeBuffer: WebGLRenderTarget, readBuffer: WebGLRenderTarget, deltaTime: number, maskActive: boolean) {
    const hidden: Object3D[] = [];
    const override = this.scene.overrideMaterial;
    const autoClear = renderer.autoClear;
    const shadowAutoUpdate = renderer.shadowMap.autoUpdate;
    const shadowNeedsUpdate = renderer.shadowMap.needsUpdate;
    const target = renderer.getRenderTarget();
    const clearColor = renderer.getClearColor(new Color());
    const clearAlpha = renderer.getClearAlpha();
    this.scene.traverseVisible((object) => {
      if (excludeFromContactShadows(object)) hidden.push(object);
    });
    hidden.forEach((object) => { object.visible = false; });
    try {
      // Reflector.onBeforeRender must never run with the normals override:
      // it would replace the mirror's image with a normal-buffer rendering.
      renderer.shadowMap.autoUpdate = false;
      renderer.shadowMap.needsUpdate = false;
      super.render(renderer, writeBuffer, readBuffer, deltaTime, maskActive);
    } finally {
      hidden.forEach((object) => { object.visible = true; });
      this.scene.overrideMaterial = override;
      renderer.autoClear = autoClear;
      renderer.shadowMap.autoUpdate = shadowAutoUpdate;
      renderer.shadowMap.needsUpdate = shadowNeedsUpdate;
      renderer.setClearColor(clearColor, clearAlpha);
      renderer.setRenderTarget(target);
    }
  }

  override dispose() {
    // r180 GTAOPass.dispose does not release these two shader materials.
    this.gtaoMaterial.dispose();
    this.blendMaterial.dispose();
    super.dispose();
  }
}

/**
 * Защита от «чёрного экрана» (29.10): если видеокарта где-то посчитает точку
 * «не числом» или бесконечностью (степень от отрицательного, деление на ноль),
 * ореол через уменьшенные копии кадра размоет её на весь экран. Перед ореолом
 * такие точки гасятся, а слишком яркие ограничиваются.
 */
export const HDR_SANITIZE_SHADER = {
  name: "EgelSanitizeHdr",
  uniforms: { tDiffuse: { value: null } },
  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
    }`,
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    varying vec2 vUv;
    void main() {
      vec4 color = texture2D( tDiffuse, vUv );
      float sum = color.r + color.g + color.b + color.a;
      // Сравнения с «не числом» ложны — проверка работает, даже если
      // компилятор выбросит isnan при быстрой математике.
      bool broken = any( isnan( color ) ) || any( isinf( color ) ) || !( sum > -1.0 && sum < 65000.0 );
      gl_FragColor = broken ? vec4( 0.0, 0.0, 0.0, 1.0 ) : clamp( color, 0.0, 256.0 );
    }`
};

/** Та же проверка — кусок шейдера для входа ореола (31.10). */
export const HDR_SANITIZE_SNIPPET = /* glsl */ `
      float egelSum = texel.r + texel.g + texel.b + texel.a;
      if ( any( isnan( texel ) ) || any( isinf( texel ) ) || !( egelSum > -1.0 && egelSum < 65000.0 ) ) texel = vec4( 0.0, 0.0, 0.0, 1.0 );
      texel = clamp( texel, 0.0, 256.0 );`;

const BLOOM_INPUT = "vec4 texel = texture2D( tDiffuse, vUv );";

/**
 * Защита от «чёрного экрана» без лишних проходов (31.10): точку «не число»
 * на весь экран размывает только ореол, поэтому проверка встроена в его вход
 * (выделение ярких точек), а не идёт двумя отдельными полноэкранными
 * проходами — на ноутбуке они заметно тормозили кадр (двери открывались
 * рывками). false — шейдер ореола другой версии, встроить не удалось.
 */
export function sanitizeBloomInput(bloom: UnrealBloomPass) {
  const material = bloom.materialHighPassFilter;
  if (material.fragmentShader.includes("egelSum")) return true;
  if (!material.fragmentShader.includes(BLOOM_INPUT)) return false;
  material.fragmentShader = material.fragmentShader.replace(BLOOM_INPUT, BLOOM_INPUT + HDR_SANITIZE_SNIPPET);
  material.needsUpdate = true;
  return true;
}

/** Linear HDR → restrained contact shading → one tone/color conversion. */
export class ScenePostProcessing {
  private readonly composer: EffectComposer;
  private readonly contactShadows: FurnitureContactShadowPass;
  /**
   * Ореол вокруг рассеивателей LED, как у объектива: порог (в линейных HDR)
   * выше любой освещённой поверхности — свет ленты ограничен «коленом»
   * экспозиции (cellLights.ts): в отсеке ≈ 1, у стены со светом на стену
   * ≈ 2,5, плюс свет комнаты — и ниже рассеивателя (яркость ×8,
   * LED_DIFFUSER_BRIGHTNESS в PlannerScene). Поэтому светится только сама
   * лента, а не белая ваза или стена рядом с ней.
   */
  private readonly lightGlow = new UnrealBloomPass(new Vector2(1, 1), 0.3, 0.4, 4.5);
  private readonly output = new OutputPass();

  constructor(private readonly renderer: WebGLRenderer, private readonly scene: Scene, private readonly camera: PerspectiveCamera) {
    const target = new WebGLRenderTarget(1, 1, {
      type: HalfFloatType,
      samples: Math.min(4, renderer.capabilities.maxSamples),
      // Трафарет — для гнёзд под чашки петель (PlannerScene, 29.10).
      stencilBuffer: true
    });
    this.composer = new EffectComposer(renderer, target);
    this.contactShadows = new FurnitureContactShadowPass(scene, camera, 1, 1);
    this.contactShadows.blendIntensity = 0.42;
    this.contactShadows.updateGtaoMaterial({
      radius: 0.08,
      thickness: 0.035,
      distanceExponent: 1.4,
      distanceFallOff: 0.8,
      scale: 1,
      samples: 16,
      screenSpaceRadius: false
    });
    this.contactShadows.updatePdMaterial({ radius: 6, samples: 24, depthPhi: 4, normalPhi: 2 });
    this.composer.addPass(new RenderPass(scene, camera));
    this.composer.addPass(this.contactShadows);
    // Испорченные точки гасятся на входе ореола; не получилось встроить —
    // отдельный проход перед ним, как в 7.48.
    if (!sanitizeBloomInput(this.lightGlow)) this.composer.addPass(new ShaderPass(HDR_SANITIZE_SHADER));
    // Лёгкий ореол как у камеры: порог проходят только HDR-рассеиватели LED и
    // их самые яркие отражения, светлая мебель целиком не «светится».
    this.composer.addPass(this.lightGlow);
    this.composer.addPass(this.output);
  }

  setSize(width: number, height: number) {
    this.composer.setSize(width, height);
    const size = contactShadowSize(width, height, this.renderer.getPixelRatio());
    this.contactShadows.setSize(size.width, size.height);
  }

  render(annotations: Object3D[], solids: Object3D[]) {
    const annotationVisibility = annotations.map((object) => object.visible);
    const solidVisibility = solids.map((object) => object.visible);
    const background = this.scene.background;
    const autoClear = this.renderer.autoClear;
    const target = this.renderer.getRenderTarget();
    try {
      annotations.forEach((object) => { object.visible = false; });
      this.composer.render();
      if (annotationVisibility.some(Boolean)) {
        // Measurement labels stay crisp and keep their original colors.
        // Snapshot methods may already have hidden them; honor that state.
        annotations.forEach((object, index) => { object.visible = annotationVisibility[index]!; });
        solids.forEach((object) => { object.visible = false; });
        this.scene.background = null;
        this.renderer.setRenderTarget(null);
        this.renderer.autoClear = false;
        this.renderer.clearDepth();
        this.renderer.render(this.scene, this.camera);
      }
    } finally {
      annotations.forEach((object, index) => { object.visible = annotationVisibility[index]!; });
      solids.forEach((object, index) => { object.visible = solidVisibility[index]!; });
      this.scene.background = background;
      this.renderer.autoClear = autoClear;
      this.renderer.setRenderTarget(target);
    }
  }

  dispose() {
    this.contactShadows.dispose();
    this.lightGlow.dispose();
    this.output.dispose();
    this.composer.dispose();
  }
}
